import type { VpIconName } from '@vtmn-play/react';
import type { AnswerType } from '~/model/AnswerType';
import type { CommentType } from '~/model/CommentType';
import type { ReactionType, ReactionValue } from '~/model/ReactionType';
import update from 'immutability-helper';

export const getReactionIcon = (
  type: ReactionValue,
  isActive = false,
): VpIconName => {
  const isDislike = type === 'dislike';
  const dislikeIcon = 'thumb-down';
  const heartIcon = isActive ? 'heart-filled' : 'heart';
  return isDislike ? dislikeIcon : heartIcon;
};

export const updateAddItem = (ideas, data) => {
  return update(ideas, { $unshift: [data] });
};

export const removeDuplicateItemsById = (
  items: AnswerType[] & CommentType[],
): AnswerType[] | CommentType[] => {
  return Array.from(new Set(items.map(a => a.id))).map(id =>
    items.find(a => a.id === id),
  );
};

export const updateAddItems = (
  ideas: AnswerType[] & CommentType[],
  data: AnswerType[] & CommentType[] = [],
  getNextPage?: boolean,
) => {
  // If new page is get, add new ideas to existing ones
  if (getNextPage) {
    return removeDuplicateItemsById([...ideas, ...data]);
  } else {
    return data;
  }
};

export const updateAddSubItems = (
  ideas: AnswerType[] & CommentType[],
  ideaUri: string,
  data: AnswerType[] & CommentType[] = [],
  pageUrl?: string,
) => {
  const ideaIndex = ideas.findIndex(i => i['@id'] === ideaUri);
  // If pageUrl is specified, add new comments to existing ones
  const ideaWithComments = pageUrl
    ? {
        ...ideas[ideaIndex],
        comments: removeDuplicateItemsById([
          ...ideas[ideaIndex].comments,
          ...data,
        ]),
      }
    : { ...ideas[ideaIndex], comments: data };
  return update(ideas, { [ideaIndex]: { $set: ideaWithComments } });
};

export const updateAddSubItem = (
  ideas: Array<AnswerType & CommentType>,
  data: AnswerType & CommentType,
  ideaId: number,
) => {
  const ideaIndex = ideas.findIndex(i => i.id === ideaId);
  return update(ideas, {
    [ideaIndex]: { comments: { $push: [data] } },
  });
};

export const updateReaction = (
  action: 'add' | 'remove',
  ideas: AnswerType[] & CommentType[],
  reaction: ReactionType,
  ideaId: number,
  commentId?: number,
) => {
  const ideaIndex = ideas.findIndex(i => i.id === ideaId);
  const commentIndex
    = ideas[ideaIndex].comments
      && ideas[ideaIndex].comments.findIndex(c => c.id === commentId);
  const updateValue = action === 'add' ? 1 : -1;
  const updatedUserReaction = action === 'add' ? reaction : null;
  return commentId
    ? update(ideas, {
        [ideaIndex]: {
          comments: {
            [commentIndex]: {
              current_user_reaction: { $set: updatedUserReaction },
              reactions_repartition: {
                [reaction.value]: { $apply: n => n + updateValue },
              },
            },
          },
        },
      })
    : update(ideas, {
        [ideaIndex]: {
          current_user_reaction: { $set: updatedUserReaction },
          reactions_repartition: {
            [reaction.value]: { $apply: n => n + updateValue },
          },
        },
      });
};

export const checkRequiredContent = (content = '') => {
  return content.trim().length > 0;
};
